import * as React from 'react';
import { ArrowLeftIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { MIN_SPLIT_PANE_PX, PHONE_MEDIA_QUERY, splitPanesFit } from '@/lib/device-viewports';
import { cn } from '@/lib/utils';

function subscribePhone(onChange: () => void) {
  const media = typeof window !== 'undefined' ? window.matchMedia?.(PHONE_MEDIA_QUERY) : undefined;
  if (!media) return () => {};
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

function readPhone() {
  return typeof window !== 'undefined' && window.matchMedia?.(PHONE_MEDIA_QUERY).matches === true;
}

/** True in the phone layout family (viewport narrower than the desktop breakpoint). */
export function useIsPhone(): boolean {
  return React.useSyncExternalStore(subscribePhone, readPhone, () => false);
}

/**
 * Whether the element is wide enough to show both split-view panes side by
 * side. Defaults to true until the first measurement (and where
 * ResizeObserver is unavailable) so desktop never flashes the stacked layout.
 */
export function useSplitPanesFit(ref: React.RefObject<HTMLElement | null>): boolean {
  const [fits, setFits] = React.useState(true);
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const update = (width: number) => setFits(width === 0 || splitPanesFit(width));
    update(element.getBoundingClientRect().width);
    const observer = new ResizeObserver(entries => {
      const entry = entries[entries.length - 1];
      if (entry) update(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return fits;
}

/**
 * Priority split view. Desktop: queue on the left, live preview on the right,
 * as long as each pane can be at least MIN_SPLIT_PANE_PX wide (for example
 * the unfolded iPhone Duo at 890px with the sidebar expanded may not be).
 * Phone, or too narrow for two panes: the queue fills the area; choosing an
 * item opens the preview with a Back button.
 */
export function SplitView({
  queue,
  preview,
  previewOpen,
  onBack,
  queueLabel = 'Queue',
  className
}: {
  queue: React.ReactNode;
  preview: React.ReactNode;
  /** Stacked layout only: whether the preview is showing instead of the queue. */
  previewOpen: boolean;
  onBack: () => void;
  queueLabel?: string;
  className?: string;
}) {
  const phone = useIsPhone();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const fits = useSplitPanesFit(containerRef);
  const stacked = phone || !fits;
  return (
    <div
      ref={containerRef}
      data-layout={stacked ? 'stacked' : 'split'}
      className={cn('flex min-h-0 min-w-0 flex-1 flex-col', className)}
    >
      {/* One stable tree for both layouts, so crossing the threshold (sidebar
          toggle, unfold, rotate) never remounts the queue or preview. */}
      <div
        className={cn(
          'grid min-h-0 flex-1',
          stacked
            ? 'grid-cols-1'
            : 'grid-cols-[minmax(var(--split-pane-min),24rem)_minmax(var(--split-pane-min),1fr)] gap-4 xl:grid-cols-[minmax(20rem,28rem)_minmax(var(--split-pane-min),1fr)]'
        )}
        style={{ '--split-pane-min': `${MIN_SPLIT_PANE_PX}px` } as React.CSSProperties}
      >
        <section
          aria-label={queueLabel}
          hidden={stacked && previewOpen}
          className={cn('flex min-h-0 min-w-0 flex-col', !stacked && 'min-h-[24rem] border-r pr-4')}
        >
          {queue}
        </section>
        <section
          aria-label="Preview"
          hidden={stacked && !previewOpen}
          className={cn('flex min-h-0 min-w-0 flex-col', stacked && 'gap-3')}
          aria-live="polite"
        >
          {stacked ? (
            <Button variant="ghost" size="sm" className="self-start" onClick={onBack}>
              <ArrowLeftIcon className="size-4" /> Back to {queueLabel.toLowerCase()}
            </Button>
          ) : null}
          {preview}
        </section>
      </div>
    </div>
  );
}
